<script lang="ts">
  import type {
    BoardAvailability,
    BoardAvailabilityWindow,
  } from "../../../../../../../../../_logic/calendar-types"

  type Availability = BoardAvailability | null
  type MaskRange = {
    startMinutes: number
    endMinutes: number
    top: number
    height: number
  }

  const clampWindowToView = (
    window: BoardAvailabilityWindow,
    startMinutes: number,
    endMinutes: number,
  ): BoardAvailabilityWindow | null => {
    const start = Math.max(startMinutes, window.startMinutes)
    const end = Math.min(endMinutes, window.endMinutes)

    if (end <= start) return null

    return { startMinutes: start, endMinutes: end }
  }

  const mergeWindows = (windows: BoardAvailabilityWindow[]) => {
    const sorted = [...windows].sort((a, b) => a.startMinutes - b.startMinutes)
    const merged: BoardAvailabilityWindow[] = []
    for (const window of sorted) {
      const previous = merged[merged.length - 1]

      if (!previous || window.startMinutes > previous.endMinutes) {
        merged.push({ ...window })
        continue
      }

      previous.endMinutes = Math.max(previous.endMinutes, window.endMinutes)
    }

    return merged
  }

  const toMaskRange = (
    startMinutes: number,
    endMinutes: number,
    viewStartMinutes: number,
    slotMinutes: number,
    slotHeight: number,
  ): MaskRange => ({
    startMinutes,
    endMinutes,
    top: ((startMinutes - viewStartMinutes) / slotMinutes) * slotHeight,
    height: ((endMinutes - startMinutes) / slotMinutes) * slotHeight,
  })

  const buildMaskRanges = ({
    availability,
    viewStartMinutes,
    viewEndMinutes,
    slotMinutes,
    slotHeight,
  }: {
    availability: Availability
    viewStartMinutes: number
    viewEndMinutes: number
    slotMinutes: number
    slotHeight: number
  }): MaskRange[] => {
    if (!availability || availability.dayOff || viewEndMinutes <= viewStartMinutes) return []

    const fallbackWindow =
      typeof availability.startMinutes === "number" && typeof availability.endMinutes === "number"
        ? [{ startMinutes: availability.startMinutes, endMinutes: availability.endMinutes }]
        : []
    const rawWindows = availability.windows?.length ? availability.windows : fallbackWindow
    const validWindows = rawWindows.filter((window) => window.endMinutes > window.startMinutes)

    if (!validWindows.length) return []

    const workingWindows = mergeWindows(
      validWindows
        .map((window) => clampWindowToView(window, viewStartMinutes, viewEndMinutes))
        .filter((window): window is BoardAvailabilityWindow => Boolean(window)),
    )

    if (!workingWindows.length) {
      return [
        toMaskRange(viewStartMinutes, viewEndMinutes, viewStartMinutes, slotMinutes, slotHeight),
      ]
    }

    const gaps: MaskRange[] = []
    let cursor = viewStartMinutes
    for (const window of workingWindows) {
      if (window.startMinutes > cursor) {
        gaps.push(
          toMaskRange(cursor, window.startMinutes, viewStartMinutes, slotMinutes, slotHeight),
        )
      }

      cursor = Math.max(cursor, window.endMinutes)
    }

    if (cursor < viewEndMinutes) {
      gaps.push(toMaskRange(cursor, viewEndMinutes, viewStartMinutes, slotMinutes, slotHeight))
    }

    return gaps
  }

  const {
    availability = null,
    viewStartMinutes,
    viewEndMinutes,
    slotMinutes,
    slotHeight,
    ariaHidden = false,
    showDayOffLabel = false,
  }: {
    availability?: Availability
    viewStartMinutes: number
    viewEndMinutes: number
    slotMinutes: number
    slotHeight: number
    ariaHidden?: boolean
    showDayOffLabel?: boolean
  } = $props()

  const maskRanges = $derived.by(() =>
    buildMaskRanges({
      availability,
      viewStartMinutes,
      viewEndMinutes,
      slotMinutes,
      slotHeight,
    }),
  )
</script>

{#if availability}
  <div class="availability-mask" aria-hidden={ariaHidden}>
    {#if availability.dayOff}
      <div class="mask off" data-availability-mask="day-off"
        >{showDayOffLabel ? "Arbeitsfrei" : ""}</div
      >
    {:else}
      {#each maskRanges as mask (`${mask.startMinutes}-${mask.endMinutes}`)}
        <div
          class="mask"
          data-availability-mask="gap"
          data-start-minutes={mask.startMinutes}
          data-end-minutes={mask.endMinutes}
          style={`height:${mask.height}px; top:${mask.top}px;`}
        ></div>
      {/each}
    {/if}
  </div>
{/if}

<style lang="less">
  .availability-mask {
    position: absolute;
    inset: 0;
    pointer-events: none;

    .mask {
      position: absolute;
      left: 0;
      right: 0;
      background: repeating-linear-gradient(
        135deg,
        color-mix(in srgb, var(--cal-text, #2f4858) 16%, transparent) 0,
        color-mix(in srgb, var(--cal-text, #2f4858) 16%, transparent) 12px,
        color-mix(in srgb, var(--cal-text, #2f4858) 6%, transparent) 12px,
        color-mix(in srgb, var(--cal-text, #2f4858) 6%, transparent) 24px
      );
      background-position: var(--mask-offset, 0px) 0;
      opacity: 0.78;
    }

    .mask.off {
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-transform: uppercase;
      font-weight: 800;
      letter-spacing: 0.08em;
      color: var(--neutral-white, #ffffff);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
      background: repeating-linear-gradient(
        135deg,
        color-mix(in srgb, var(--cal-accent-soft, #ad514c) 38%, transparent) 0,
        color-mix(in srgb, var(--cal-accent-soft, #ad514c) 38%, transparent) 16px,
        color-mix(in srgb, var(--cal-accent-soft, #ad514c) 22%, transparent) 16px,
        color-mix(in srgb, var(--cal-accent-soft, #ad514c) 22%, transparent) 32px
      );
      background-position: var(--mask-offset, 0px) 0;
      opacity: 0.9;
    }
  }
</style>
